import type { ChartSeriesData } from "@core/apis/shared";
import {
  MeterDataComponent,
  SensorDataPerSubstation,
  type RadiatorCurve,
  type Sensor,
} from "@core/apis/shared";
import { Coordinates } from "@core/types/common";

// Local types specific to radiator curve functionality
export type Substation = {
  uid: string;
  coordinates?: [number, number];
};

// Constants
const DEFAULT_OUTDOOR_TEMP = -2.5;

// Coordinate utilities
export function extractCoordinates(substation?: Substation): Coordinates | null {
  if (!substation?.coordinates) return null;

  return {
    lat: substation.coordinates[0],
    lon: substation.coordinates[1],
  };
}

// Temperature utilities
export function extractLatestTemperature(weatherData: Array<{ value: number }>): number {
  if (!weatherData?.length) {
    return DEFAULT_OUTDOOR_TEMP;
  }

  const latestReading = weatherData[weatherData.length - 1];
  return Math.round(latestReading.value * 10) / 10;
}

// Radiator curve utilities
export function extractCurvePoints(radiatorCurves: RadiatorCurve[]) {
  const firstCurve = radiatorCurves?.[0];
  const breakpoints = firstCurve?.model_data?.breakpoints || [];

  const validPoints = breakpoints.filter(
    (point) =>
      Number.isFinite(point.outdoor_temperature) && Number.isFinite(point.supply_temperature)
  );

  return validPoints.sort((a, b) => a.outdoor_temperature - b.outdoor_temperature);
}

// Meter data utilities
export function formatMeterData(rawMeterData: MeterDataComponent[]): ChartSeriesData[] {
  if (!rawMeterData?.length) {
    return [];
  }

  return rawMeterData.map((component) => {
    const sortedData = [...component.data].sort(
      (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    );

    return {
      uid: component.resource_uid,
      meter: component.meter_name,
      stage: component.stage_name,
      component: component.component_name,
      variant: component.variant_name,
      data: sortedData.map((point) => ({
        datetime: point.datetime,
        value: point.value,
        flags: [],
      })),
    };
  });
}

export function extractHeatEnergyData(
  formattedMeterData: ChartSeriesData[]
): Array<[number, number]> {
  const heatEnergyComponent = formattedMeterData.find(
    (component) => component.component === "heat_energy"
  );

  if (!heatEnergyComponent) {
    return [];
  }

  return heatEnergyComponent.data.map((point) => [new Date(point.datetime).getTime(), point.value]);
}

export function extractSupplyTemperatureData(
  formattedMeterData: ChartSeriesData[]
): Array<{ datetime: string; value: number }> {
  const supplyTempComponent = formattedMeterData.find(
    (component) => component.component === "supplytemp"
  );

  if (!supplyTempComponent) {
    return [];
  }

  return supplyTempComponent.data.map((point) => ({
    datetime: point.datetime,
    value: point.value,
  }));
}

export function combineSupplyAndOutdoorData(
  supplyTempData: Array<{ datetime: string; value: number }>,
  outdoorTempData: Array<{ datetime: string; value: number }>
): Array<{ outdoor_temperature: number; supply_temperature: number }> {
  if (!supplyTempData?.length || !outdoorTempData?.length) {
    return [];
  }

  const weatherPoints = outdoorTempData
    .map((point) => {
      const timestamp = Date.parse(point.datetime);
      return Number.isNaN(timestamp) ? null : { timestamp, value: point.value };
    })
    .filter((point): point is { timestamp: number; value: number } => point !== null)
    .sort((a, b) => a.timestamp - b.timestamp);

  const weatherByTimestamp = new Map(weatherPoints.map((point) => [point.timestamp, point.value]));

  const NEAREST_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

  const findNearestWeather = (timestamp: number): number | undefined => {
    if (weatherPoints.length === 0) return undefined;

    let left = 0;
    let right = weatherPoints.length - 1;
    let bestMatch: { timestamp: number; value: number } | undefined;
    let bestDiff = Number.POSITIVE_INFINITY;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const candidate = weatherPoints[mid];
      const diff = Math.abs(candidate.timestamp - timestamp);

      if (diff < bestDiff) {
        bestDiff = diff;
        bestMatch = candidate;
      }

      if (candidate.timestamp === timestamp) {
        break;
      }

      if (candidate.timestamp < timestamp) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    if (bestMatch && bestDiff <= NEAREST_THRESHOLD_MS) {
      return bestMatch.value;
    }

    return undefined;
  };

  // Combine supply temp with corresponding outdoor temp
  return supplyTempData.reduce<Array<{ outdoor_temperature: number; supply_temperature: number }>>(
    (acc, supplyPoint, index) => {
      const supplyTimestamp = Date.parse(supplyPoint.datetime);
      let outdoorTemp: number | undefined;

      if (!Number.isNaN(supplyTimestamp)) {
        outdoorTemp = weatherByTimestamp.get(supplyTimestamp);
        if (outdoorTemp === undefined) {
          outdoorTemp = findNearestWeather(supplyTimestamp);
        }
      }

      if (outdoorTemp === undefined) {
        const weatherFallback = outdoorTempData[index];
        outdoorTemp = weatherFallback?.value;
      }

      if (outdoorTemp !== undefined) {
        acc.push({
          outdoor_temperature: outdoorTemp,
          supply_temperature: supplyPoint.value,
        });
      }

      return acc;
    },
    []
  );
}

// Sensor utilities
export function findRadiatorSupplyTempSensor(sensors: Sensor[]): Sensor | undefined {
  return sensors.find(
    (sensor) => sensor.sensor_type_technical_name === "radiator_system_supply_temp"
  );
}

export function extractRadiatorSupplyTempFromSensorData(
  sensorData: SensorDataPerSubstation[]
): Array<{ datetime: string; value: number }> {
  if (!sensorData?.length) {
    return [];
  }

  return sensorData
    .filter((reading) => reading.values.radiator_system_supply_temp !== undefined)
    .map((reading) => ({
      datetime: reading.timestamp,
      value: reading.values.radiator_system_supply_temp!,
    }));
}
